import ConstData from "../ConstData";
import { enWebPage, stQuizDataType, stUserStatus } from "../ConstCommand";

import { getIsForPblModeBrowser, isAndroidWebView, isIOSWebView } from "../HandsUpClient";

/**
 * 빈 Object 유형의 객체인지 여부를 확인 후 반환하는 메서드
 * - Object 유형일 경우, 빈 객체인 경우 true를 반환한다.
 * - Object 유형이 아니거나(null, undefined 포함됨),
 *   빈 Object 유형의 객체가 아닐 경우 false를 반환한다.
 * 
 * @param {Object} obj - 확인할 변수
 * @returns {Boolean} 빈 Object 유형의 객체인지 여부
 */
export const isEmptyObj = (obj) => {
    return (
        obj !== null &&
        obj !== undefined &&
        obj.constructor === Object &&
        Object.keys(obj).length === 0
    );
}

/**
 * 빈 문자열이 아닌지 여부를 확인하는 메서드.
 * 
 * @param {String} str - 확인할 문자열
 * @param {Boolean} [trim=true] - 공백을 제거 후 검사할지 여부
 * @returns {Boolean} 빈 문자열이 아니면 true
 * @since 2026-04-08.
 */
export const isNotEmptyStr = (str, trim = true) => {
    if (typeof str !== "string") return false;
    return trim ? str.trim().length > 0 : str.length > 0;
}

/**
 * 배열의 길이가 매개변수로 전달받은 최솟값({@code min}), 
 * 최댓값({@code max})을 모두 만족하는지 여부를 반환한다.
 * 
 * @param {Array} arr - 검사할 배열
 * @param {Number} min - 배열의 최소 길이
 * @param {Number} max - 배열의 최대 길이
 * @returns {Boolean} Path 배열이 조건을 만족하는지 여부
 * @since 2026-04-08.
 */
export const isValidArray = (arr, min, max) => {
    return Array.isArray(arr) && arr.length >= min && arr.length <= max;
}

/**
 * 사용 가능한 배열 객체인지 여부를 확인 후 반환하는 메서드
 * - 배열일 경우 길이가 1이상인 경우에만 true를 반환한다.
 * - 배열이 아니거나(null, undefined 포함됨),
 *   배열의 길이가 0인 경우 false를 반환한다.
 * 
 * @param {Object} obj - 확인할 객체
 * @returns {Boolean} 사용 가능한 배열 객체인지 여부
 * @since 2025-11-13.
 */
export const isUsableArrayObj = (obj) => {
    // if (obj === null || obj === undefined) return false;

    if (Array.isArray(obj) && obj.length > 0) {
        return true;
    }

    return false;
}

/**
 * 개발용 버전인지 여부를 반환하는 메서드
 * 
 * @returns 개발용 버전인지 여부
 * @since 2025-08-13.
 * @lastModified 2026-04-09.
 * @changelog
 * Note: 업데이트 기록
 * 
 *     • 2026-04-09:
 *       ￮ 주석 처리된 코드 모두 제거
 *       ￮ 개발용 버전 여부 확인용으로 사용되도록 수정
 *       ￮ by hjkim
 *     • 2025-08-13:
 *       ￮ 메서드 최초 구현
 *       ￮ ConstData.IS_FOR_DEV && ConstData.IS_DEV_VALUE 또는 ConstData.IS_DEV_VALUE 를 반환하도록 구현
 *       ￮ by hjkim
 */
export const isDevVersion = () => {
    // return ConstData.IS_FOR_DEV && ConstData.IS_DEV_VALUE;
    return ConstData.IS_DEV_VALUE;
}

/**
 * PC에서 Pentalk Browser 프로그램으로 접속했는지 여부를 반환하는 메서드
 * 
 * @returns {Boolean} PC Pentalk Browser 프로그램으로 접속했는지 여부
 * @since 2026-02-19.
 */
export const isWindowsWebView = () => {
    return window.chrome && window.chrome.webview;
}

/**
 * 260918 학생 앱(Windows Pentalk Browser / Android AndWebnote2606)으로 kind 메시지를 보낸다.
 * 학생 앱이 아니면 아무것도 하지 않는다 — 일반 브라우저에서 불러도 안전하다.
 *
 * 전송로만 다르고 메시지 객체는 양쪽이 같다.
 *  - Windows: chrome.webview.postMessage(객체)
 *  - Android: AndroidLogin.onWebMessage(JSON 문자열)
 *  - iOS:     webkit.messageHandlers.screenShareHandler.postMessage({ action: "pentalkAgent", ...객체 })  (261021)
 *
 * Android 판별에 isPentalkAndr()를 쓰면 안 된다 — 그건 window.ProcessComm 유무라
 * 옛 Pro 앱 기준이고, AndWebnote2606은 ProcessComm을 넣지 않아 항상 false다.
 * 이 앱은 window.AndroidLogin을 넣으므로 그 유무로 가른다.
 *
 * 요청: PentalkAgentAndroid\docs\external\web-frontend-android-bridge-request-20260918.md §4
 *
 * @param {Object} msg - { kind, ... } 학생 앱과 합의된 메시지 객체
 * @returns {Boolean} 실제로 보냈는지 여부
 * @since 2026-09-18.
 */
export const sendToStudentApp = (msg) => {
    if (isWindowsWebView()) {
        window.chrome.webview.postMessage(msg);
        return true;
    }

    if (window.AndroidLogin && typeof window.AndroidLogin.onWebMessage === "function") {
        window.AndroidLogin.onWebMessage(JSON.stringify(msg));
        return true;
    }

    // 261021 iOS 학생 앱(PentalkOnScreen) — 화면 공유 등에 이미 쓰는 screenShareHandler 통로로 보내고, action 으로 구분한다.
    // JSON 문자열이 아니라 객체로 보낸다(WKWebView 가 그대로 사전으로 넘긴다). 나머지 필드는 Windows·Android 와 같다.
    // ⚠ iOS 는 MQ 를 네이티브가 쥐므로, 앱이 알린 pro-disconnectedRbmq 로 onConnectionLost 가 불려 conn-lost 가 앱에 되돌아간다.
    // 요청: PentalkAgent\docs\external\web-frontend-ios-bridge-request-20261021.md §4
    if (hasIOSStudentAppBridge()) {
        window.webkit.messageHandlers.screenShareHandler.postMessage({ action: "pentalkAgent", ...msg });
        return true;
    }

    return false;
}

/**
 * iOS 학생 앱(PentalkOnScreen)의 WKWebView 안인지 반환한다 — screenShareHandler 메시지 핸들러가 있으면 그렇다.
 * 일반 Safari 에는 이 객체가 없다.
 *
 * HandsUpClient 의 isIOSWebView() 와 같은 기준이지만 여기 따로 둔다 — 이 유틸 모듈이
 * 거대 모듈의 판별 함수에 기대면 학생 앱 발신 판별이 그쪽 로드 순서에 묶인다.
 *
 * @returns {Boolean} iOS 학생 앱 브리지가 있는지 여부
 * @since 2026-10-21.
 */
export const hasIOSStudentAppBridge = () => {
    return !!(window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.screenShareHandler);
}

/**
 * 접속한 펜톡 서버의 주소를 "."으로 split한 배열을 통해,
 * 서브 도메인 값을 찾아서 반환한다.
 * 
 * @param {Array} urlArr - "."으로 split된 문자열 배열
 * @returns {String} 서브 도메인 값
 */
export const getUserKindFromURL = (urlArr) => {
    // console.log("getUserKindFromURL - ", urlArr);
    try {
        if (ConstData.IS_LOCAL_VERSION) {
            return "local";
        } else {
            if (urlArr !== undefined && urlArr !== null && urlArr.length > 0) {
                if (urlArr[0] === "www") {
                    if (urlArr[1] === "hiclass" || urlArr[1] === "test" || urlArr[1] === "localhost") {
                        return "hiclasstv";
                    } else {
                        return urlArr[1];
                    }
                } else {
                    if (urlArr[0] === "hiclass" || urlArr[0] === "test" || urlArr[0] === "localhost" || !isNaN(urlArr[0])) {
                        return "hiclasstv";
                    } else {
                        return urlArr[0];
                    }
                }
            } else {
                console.log("getUserKindFromURL - urlArr => ", urlArr);
                return "hiclasstv";
            }
        }
    } catch (err) {
        console.log("canUserSignUp - urlArr[", urlArr, "] check err => ", err);
        return "hiclasstv";
    }
}

/**
 * 사용자 서브 도메인 유형에 따른 로고 이미지 URL을 반환하는 메서드
 * 
 * @param {String} userSubDomain - 사용자 서브 도메인 유형
 * @returns {String} 완성된 로고 이미지 URL 반환
 * @since 2025-08-06.
 */
export const getLogoImgURLFromUserKind = (userSubDomain) => {
    // console.log("getLogoImgURLFromUserKind - isLocalVersion => ", ConstData.IS_LOCAL_VERSION);

    let logoImgUrlPath = "";

    if (ConstData.IS_LOCAL_VERSION && ConstData.IS_FOR_DEV) {
        logoImgUrlPath = ConstData.LOCAL_HOST_ADD + ConstData.LOGO_IMG_DIR + userSubDomain + ".png";
        // console.log("getLogoImgURLFromUserKind - case 1:) logoImgUrlPath => ", logoImgUrlPath);
    } else {
        if (userSubDomain === "hiclasstv"/*  || userKind === "local" */) {
            logoImgUrlPath = "/images/hiclassTV.png";
            // console.log("getLogoImgURLFromUserKind - case 2:) logoImgUrlPath => ", logoImgUrlPath);
        } else {
            logoImgUrlPath = ConstData.MAIN_HOST_ADD + ConstData.LOGO_IMG_DIR + userSubDomain + ".png";
            // console.log("getLogoImgURLFromUserKind - case 3:) logoImgUrlPath => ", logoImgUrlPath);
        }
    }

    return logoImgUrlPath;
}

/**
 * 이지시스템의 copyright 연도 정보를 현재 연도와 함께 반환하는 메서드.
 * 
 * @returns {String} copyright 연도 정보
 * @since 2025-11-26.
 */
export const getEasyCoptyRightWithCurrentYear = () => {
    const date = new Date();
    const yaer = date.getFullYear();
    // console.log("getEasyCoptyRightWithCurrentYear - year[", yaer, "]");
    return `1992~${yaer}`;
}

/**
 * 전달받은 멤버 정보 목록에서, 현재 접속한 멤버의 수를 반환한다.
 * 
 * @param {String} type - 멤버 정보의 유형
 * @param {Array} list_member - 멤버 정보가 포함된 배열
 * @returns {Number} 현재 접속한 멤버의 수
 */
export const getEntryMemberCount = (type, list_member) => {
    let cnt = 0;

    if (list_member !== undefined && list_member !== null) {
        if (type === "handsUp") {
            list_member.forEach(member => {
                if (member.userJoinStatus === "Y") {
                    cnt++;
                }
            });
        } else if (type === "teamUp") {
            list_member.forEach(member => {
                if (member.entryYN === "Y") {
                    cnt++;
                }
            });
        } else if (type === "userStatus") {
            list_member.forEach(member => {
                if (member.userStatus !== stUserStatus.LogOut || member.webUserStatus !== stUserStatus.LogOut) {
                    cnt++;
                }
            });
        }
    }

    return cnt;
}

/**
 * 전달받은 멤버 정보 목록에서, 멤버의 수(= 배열의 길이)를 반환한다.
 * 
 * @param {Array} list_member - 멤버 정보가 포함된 배열
 * @returns {Number} 멤버의 수
 */
export const getMemberCount = (list_member) => {
    return list_member === undefined || list_member === null ? 0 : list_member.length;
}

/**
 * 전달받은 프로필 이미지 순번을 포함하여, 프로필 이미지 경로를 반환한다.
 * 
 * @param {String|Number} iconSeq - 프로필 이미지 순번
 * @returns {String} 프로필 이미지 경로
 */
export const getMemberProfileImage = (iconSeq) => {
    if (iconSeq !== undefined && iconSeq !== null && iconSeq !== "") {
        return ConstData.IMG_DIR + "profile_0" + iconSeq + ".png";
    } else {
        return ConstData.IMG_DIR + "profile_01.png";
    }
}

/**
 * 부동소수점 숫자를 반올림 후 고정 소수점 형식으로 반환한다.
 * 
 * @param {Number} value - 처리할 부동소수점 숫자
 * @param {Number} fixed - 반환할 소수점 이하 자릿수
 * @returns {String} 반올림 후 고정 소수점 형식의 문자열
 * @modify 2026-02-27.
 */
export const getFloatFixed = (value, fixed) => {
    // return parseFloat(Math.round(value * 100) / 100).toFixed(fixed);

    const factor = Math.pow(10, fixed); // 10^fixed (자릿수 기준 배수)
    return parseFloat(Math.round(value * factor) / factor).toFixed(fixed);
}

/**
 * Base64 문자열을 Blob으로 변환하는 헬퍼 함수
 * 
 * @param {String} base64 - Base64 인코딩된 문자열
 * @param {String} mimeType - 파일의 MIME 타입
 * @returns {Blob} 변환된 Blob 객체
 * @since 2026-01-07.
 */
export const base64ToBlob = (base64, mimeType) => {
    // Base64 디코딩
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);

    // Blob 생성
    return new Blob([byteArray], { type: mimeType });
}

export const canUserSignUp = (userSubDomain) => {
    // console.log("canUserSignUp - ", userSubDomain);
    try {
        if (ConstData.IS_LOCAL_VERSION) {
            return false;
        } else {
            if (userSubDomain !== undefined && userSubDomain !== null && userSubDomain.length > 0) {
                if (userSubDomain[0] === "www") {
                    if (userSubDomain[1] === "hiclass") {
                        return false;
                    } else if (userSubDomain[1] === "test" || userSubDomain[1] === "localhost") {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    if (userSubDomain[0] === "hiclass") {
                        return false;
                    } else if (userSubDomain[0] === "test" || userSubDomain[0] === "localhost" || !isNaN(userSubDomain[0])) {
                        return true;
                    } else {
                        return false;
                    }
                }
            } else {
                console.log("getUserKindFromURL - userSubDomain => ", userSubDomain);
                return false;
            }
        }
    } catch (err) {
        console.log("canUserSignUp - userSubDomain[", userSubDomain, "] check err => ", err);
        return false;
    }
}

/**
 * 전체 화면 모드로 전환을 요청한다.
 */
export const callRequestFullScreen = async () => {
    // console.log("request full screen func start");
    if (document.body) {
        /** full screen 지원 & full screen 모드가 아닐 때 요청 by hjkim 20210331 */
        if (document.fullscreenEnabled && document.fullscreenElement === null) {
            try {
                let promise = await document.body.requestFullscreen();
                console.log("request full screen func finish");
                console.log(promise);
            } catch (err) {
                console.log("could not use full screen mode");
                console.log(err);
            }
        } else if (document.webkitFullscreenEnabled && document.webkitFullscreenElement === null) {
            try {
                let promise = await document.body.webkitRequestFullscreen();
                console.log("full screen func finish - webkit");
                console.log(promise);
            } catch (err) {
                console.log("could not use full screen mode - webkit");
                console.log(err);
            }
        } else if (document.mozFullScreenEnabled && document.mozFullScreenElement === null) {
            try {
                let promise = await document.body.mozRequestFullScreen();
                console.log("request full screen func finish - moz");
                console.log(promise);
            } catch (err) {
                console.log("could not use full screen mode - moz");
                console.log(err);
            }
        } else if (document.msFullscreenEnabled && document.msFullscreenElement === null) {
            try {
                let promise = await document.body.msRequestFullscreen();
                console.log("request full screen func finish - ms");
                console.log(promise);
            } catch (err) {
                console.log("could not use full screen mode - ms");
                console.log(err);
            }
        } else {
            // console.log("document.body request full screen type else");
        }
    }
}

/**
 * 전체 화면 모드를 종료하도록 요청한다.
 */
export const callExitFullScreen = async () => {
    // console.log("exit full screen func start");
    if (document.body) {
        /** full screen 지원 & full screen 모드일 때 요청 by hjkim 20210331 */
        if (document.fullscreenEnabled && document.fullscreenElement !== null) {
            try {
                let promise = await document.exitFullscreen();
                console.log("exit full screen func finish");
                console.log(promise);
            } catch (err) {
                console.log("could not use exit full screen mode");
                console.log(err);
            }
        } else if (document.webkitFullscreenEnabled && document.webkitFullscreenElement !== null) {
            try {
                let promise = await document.webkitExitFullscreen();
                console.log("exit full screen func finish - webkit");
                console.log(promise);
            } catch (err) {
                console.log("could not use exit full screen mode - webkit");
                console.log(err);
            }
        } else if (document.mozFullScreenEnabled && document.mozFullScreenElement !== null) {
            try {
                let promise = await document.mozCancelFullScreen();
                console.log("exit full screen func finish - moz");
                console.log(promise);
            } catch (err) {
                console.log("could not use cancel full screen mode - moz");
                console.log(err);
            }
        } else if (document.msFullscreenEnabled && document.msFullscreenElement !== null) {
            try {
                let promise = await document.msExitFullscreen();
                console.log("exit full screen func finish - ms");
                console.log(promise);
            } catch (err) {
                console.log("could not use exit full screen mode - ms");
                console.log(err);
            }
        } else {
            // console.log("document.body exit full screen type else");
        }
    }
}

/**
 * 비디오 요소에 대해, PIP 모드를 요청한다.
 * 
 * @param {HTMLVideoElement} videoEl - HTML 비디오 요소 객체
 */
export const callRequestPictureInPicture = async (videoEl) => {
    if (document.body) {
        /** pip 지원 & pip 모드가 아닐 때 요청 by hjkim 20211021 */
        if (document.pictureInPictureEnabled && document.pictureInPictureElement === null) {
            try {
                let promise = await videoEl.requestPictureInPicture();
                console.log("request picture in picture func finish");
                console.log(promise);
            } catch (err) {
                console.log("could not use picture in picture mode");
                console.log(err);
            }
        } else if (document.webkitPictureInPictureEnabled && document.webkitPictureInPictureElement === null) {
            try {
                let promise = await videoEl.webkitRequestPictureInPicture();
                console.log("exit full screen func finish - webkit");
                console.log(promise);
            } catch (err) {
                console.log("could not use exit full screen mode - webkit");
                console.log(err);
            }
        } else {
            console.log("not supported pip");
        }
    }
}

/**
 * PIP 모드를 종료하도록 요청한다.
 */
export const callExitPictureInPicture = async () => {
    if (document.pictureInPictureEnabled && document.pictureInPictureElement !== null) {
        document.exitPictureInPicture();
    } else if (document.webkitPictureInPictureEnabled && document.webkitPictureInPictureElement !== null) {
        try {
            let promise = await document.webkitExitFullscreen();
            console.log("exit full screen func finish - webkit");
            console.log(promise);
        } catch (err) {
            console.log("could not use exit full screen mode - webkit");
            console.log(err);
        }
    }
}

/**
 * 브라우저의 Permissions API 지원 여부를 확인하고,
 * 미지원 환경에서도 안전하게 동작하도록 폴백(fallback)을 설정한다.
 */
export const callBuildPermissionsQuery = () => {
    if (navigator.permissions === undefined || navigator.permissions === null) {
        navigator.permissions = {};
    }

    if (navigator.permissions.query === undefined || navigator.permissions.query === null) {
        navigator.permissions.query = () => {
            return { state: "permissions-query-not-supported" };
        };
    }
}

/**
 * 변수의 값이 유효한 문자열인지 여부를 반환한다.
 * 
 * @param {String|any} value - 검사할 대상
 * @returns {Boolean} 유효한 문자열인지 여부
 * @since 2026-02-27.
 */
export const checkStringValidation = (value) => {
    // return value !== undefined && value !== null && value !== "";
    return typeof value === "string" && value !== "";
}

/**
 * 문자열의 길이가 매개변수로 전달받은 최솟값({@code min}), 최댓값({@code max})을
 * 모두 만족하는지 여부를 반환한다.
 * 
 * @param {String} value - 검사할 문자열
 * @param {Number} min - 문자열의 최소 길이
 * @param {Number} max - 문자열의 최대 길이
 * @returns {Boolean} 문자열이 조건을 만족하는지 여부
 * @since 2026-02-27.
 */
export const checkStringLengthValidation = (value, min, max) => {
    // return value.length >= min && value.length <= max;
    return typeof value === "string" && value.length >= min && value.length <= max;
}

/**
 * 변수의 값이 유효한 숫자인지 여부를 반환한다.
 * 
 * @param {Number|any} value - 검사할 대상
 * @returns {Boolean} 유효한 숫자인지 여부
 * @since 2026-02-27.
 */
export const checkNumberValidation = (value) => {
    // return value !== undefined && value !== null && value > -1;
    return typeof value === "number" && value > -1;
}

/**
 * 변수의 값이 유효한 불리언(Boolean)인지 여부를 반환한다.
 * 
 * @param {Boolean|any} value - 검사할 대상
 * @returns {Boolean} 유효한 불리언(Boolean)인지 여부
 * @since 2026-02-27.
 */
export const checkBooleanValidation = (value) => {
    // return value !== undefined && value !== null && typeof value === "boolean";
    return typeof value === "boolean";
}

/**
 * 비밀번호가 펜톡의 비밀번호 요건을 만족하는지 여부를 반환한다.
 * 
 * @param {String} str - 검사할 문자열
 * @returns {Boolean} 비밀번호가 조건을 만족하는지 여부
 * @since 2026-02-27.
 */
export const checkPasswordPattern = (str) => {
    let pattern1 = /[0-9]/;                         // 숫자
    let pattern2 = /[a-zA-Z]/;			            // 문자
    let pattern3 = /[~!@#$%^&*()_+|<>?:{}]/;        // 특수문자

    if (!pattern1.test(str) || !pattern2.test(str) || !pattern3.test(str) || str.length < 8) {
        // alert("비밀번호는 8자리 이상 문자, 숫자, 특수문자로 구성하여야 합니다.");
        return false;
    } else {
        return true;
    }
}

/**
 * 파일 업로드가 완료된 후 QuizDataType을 URL로 변경해야 하는지 여부를 반환한다.
 * - for Quiz
 * 
 * @param {String|any} value - 검사할 대상
 * @returns {Boolean} QuizDataType을 URL로 변경해야 하는지 여부
 * @since 2026-02-27.
 */
export const checkConvertDataType = (value) => {
    return checkStringValidation(value) && value !== stQuizDataType.URL;
}

/**
 * URL 형식의 문자열인지 여부를 반환한다.
 * 
 * @param {String} value - 검사할 문자열
 * @returns {Boolean} URL 형식의 문자열인지 여부
 * @since 2026-02-27.
 */
export const checkURLValidation = (value) => {
    let isUrlText = false;
    try {
        const regExp =
            /(?:(?:(https?|ftp|telnet):\/\/|[^\s\t\r\n[\]`<>"'])((?:[\w$\-_.+!*'(),]|%[0-9a-f][0-9a-f])*:(?:[\w$\-_.+!*'(),;?&=]|%[0-9a-f][0-9a-f])+@)?(?:((?:(?:[a-z0-9\-가-힣]+\.)+[a-z0-9-]{2,})|(?:[\d]{1,3}\.){3}[\d]{1,3})|localhost)(?::([0-9]+))?((?:\/(?:[\w$\-_.+!*'(),;:@&=ㄱ-ㅎㅏ-ㅣ가-힣]|%[0-9a-f][0-9a-f])+)*)(?:\/([^\s/?.:<>|#]*(?:\.[^\s/?:<>|#]+)*))?(\/?[?;](?:[a-z0-9-]+(?:=[^\s:&<>]*)?&)*[a-z0-9-]+(?:=[^\s:&<>]*)?)?(#[\w-]+)?)/gmi;
        let arr = value.match(regExp);
        console.log("checkURLValidation - value[", value, "], arr => ", arr);
        if (arr && arr.length > 0) {
            // url = text.match(regExp)[1];
            isUrlText = true;
        }
    } catch (err) {
        console.log("checkURLValidation - err => ", err);
    }

    return isUrlText;
}

/**
 * 다운로드 가능한 Url인지 여부를 반환합니다.
 * 
 * @param {String} text - 검사할 문자열
 * @returns {Boolean} 다운로드 가능한 Url인지 여부
 * @since 2026-01-09.
 */
export const checkDownloadableUrl = (text) => {
    try {
        // 전체 URL 경로를 캡처하는 정규식
        const urlRegExp = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;
        const urls = text.match(urlRegExp);

        console.log("checkDownloadableUrl - matched URLs => ", urls);

        // URL이 존재하고 '/download/'를 포함하는지 체크
        if (urls && urls.length > 0) {
            return urls.some(url => url.includes('/download/'));
        }

        return false;
    } catch (err) {
        console.log("checkDownloadableUrl - err => ", err);
        return false;
    }
}

/**
 * URL 형식의 문자열이 포함되어 있는지 여부를 반환한다.
 * 
 * @param {String} text 
 * @returns {Boolean} URL 형식 문자열인지 여부
 */
export const checkUrlText = (text) => {
    let isUrlText = false;
    let url = "";

    try {
        // let regExp = new RegExp(/(https?:\/\/[^ ]*)/, "gi");
        // let regExp = new RegExp(/((http|https|ftp):\/\/[\w?=&.\/-;#~%-]+(?![\w\s?&.\/;#~%"=-]*>))/, "gi");
        let regExp = new RegExp(/(http(s)?:\/\/)([a-z0-9\w]+\.*)+[a-z0-9]{2,4}/, "gi");
        // let urlRegex = /(https?:\/\/[^ ]*)/;
        let arr = text.match(regExp);
        // console.log(arr);
        if (arr && arr.length > 0) {
            // url = text.match(regExp)[1];
            isUrlText = true;
        }
        console.log("getUrlText - arr => ", arr);

        // if (url && url !== "") isUrlText = true;
    } catch (err) {
        console.log("checkUrlText - err => ", err);
    }

    return isUrlText;
}

/**
 * 문자열에서 URL 주소를 추출하여 반환한다.
 * 
 * @param {String} text - URL이 포함된 문자열
 * @returns {String} 추출된 URL 문자열, 없으면 빈 문자열 반환
 */
export const getUrlText = (text) => {
    let url = "";

    try {
        // let regExp = new RegExp(/(https?:\/\/[^ ]*)/, "gi");
        // let regExp = new RegExp(/((http|https|ftp):\/\/[\w?=&.\/-;#~%-]+(?![\w\s?&.\/;#~%"=-]*>))/, "gi");
        let regExp = new RegExp(/(http(s)?:\/\/)([a-z0-9\w]+\.*)+[a-z0-9]{2,4}/, "gi");
        // let urlRegex = /(https?:\/\/[^ ]*)/g;
        let arr = text.match(regExp);
        // console.log(arr);
        if (arr && arr.length > 0) {
            url = text.match(regExp)[1];
        }
        console.log("getUrlText - detected url[", url, "] from arr => ", arr);
    } catch (err) {
        console.log("getUrlText - err => ", err);
    }

    return url;
}

/**
 * 현재 모바일 기기로 웹 브라우저에 접속했는지 여부를 반환한다.
 * 
 * @returns {Boolean} 모바일 기기로 웹 브라우저에 접속했는지 여부
 * @since 2025-08-01.
 */
export const getIsMobile = () => {
    /* let isMobile = (navigator.userAgent.toLowerCase().indexOf("android") > -1 || navigator.userAgent.toLowerCase().indexOf("iphone") > -1 || (navigator.userAgent.toLowerCase().indexOf("macintosh") > -1 && navigator.maxTouchPoints > 2));
    console.log("isMobile => ", isMobile);
    return isMobile; */

    return (navigator.userAgent.toLowerCase().indexOf("android") > -1 || navigator.userAgent.toLowerCase().indexOf("iphone") > -1 || (navigator.userAgent.toLowerCase().indexOf("macintosh") > -1 && navigator.maxTouchPoints > 2));
}

/**
 * URL에서 파일명만 추출하는 내부 헬퍼
 * @param {string} fileUrl - 다운로드 URL
 * @returns {string} 디코딩된 파일명
 * @since 2026-06-04.
 */
const getFileNameFromUrl = (fileUrl) => {
    try {
        // URL 객체로 파싱해서 pathname에서 파일명 추출 (쿼리스트링 등 제거)
        const urlObj = new URL(fileUrl, ConstData.MAIN_HOST_ADD);
        const pathname = urlObj.pathname;
        const filename = pathname.substring(pathname.lastIndexOf("/") + 1);
        return decodeURIComponent(filename);
    } catch (err) {
        // URL 파싱 실패 시 단순 substring으로 추출
        const filename = fileUrl.substring(fileUrl.lastIndexOf("/") + 1);
        return decodeURIComponent(filename);
    }
}

/**
 * 파일명에서 OS/브라우저에서 허용되지 않는 문자를 제거하는 내부 헬퍼
 * @param {string} filename - 원본 파일명
 * @returns {string} 정제된 파일명
 * @since 2026-06-04.
 */
const sanitizeFileName = (filename) => {
    if (!filename) return "";
    return filename
        .trim()
        // Windows / Android / iOS 에서 파일명으로 사용 불가한 특수문자 → '_' 로 치환
        .replace(/[\\/:*?"<>|]/g, "_")
        // 제어 문자 제거
        .replace(/[\x00-\x1f\x80-\x9f]/g, "_");
}

/**
 * 최종 사용할 파일명 결정 헬퍼
 * @param {string} url - 다운로드 URL
 * @param {string|null} originalFileName - 서버에서 받은 원본 파일명 (없으면 null)
 * @returns {string} 최종 파일명
 * @since 2026-06-04.
 */
const getFinalFileName = (url, originalFileName) => {
    // originalFileName이 있으면 우선 사용, 없으면 URL에서 추출
    let filename = isNotEmptyStr(originalFileName)
        ? originalFileName
        : getFileNameFromUrl(url);

    filename = sanitizeFileName(filename);

    // 정제 후에도 비어있으면 기본 파일명 사용
    if (!filename) filename = "download_file";

    return filename;
}

/**
 * [핵심 공용 함수]
 * fetch → Blob → createObjectURL → <a> 클릭 방식으로 파일을 다운로드합니다.
 * ClassTalkContainer.handleFileDownload와 rcvChatFile(자동 다운로드) 양쪽에서 호출 가능.
 *
 * @param {string} url - 다운로드할 파일의 URL
 * @param {string|null} originalFileName - 저장될 파일명 (없으면 URL에서 추출)
 * @param {Boolean} [isOverwriteAllowed=true] - 기존 파일 덮어쓰기를 허용할지 여부 (기본값: true)
 * @param {Boolean} [executeYn="N"] - 파일을 실행할지 여부 (기본값: "N")
 * @param {Object} [contentInfo=null] - 컨텐츠 정보 (contentInfo: { contentSeq, contentTeacherSeq, refContentSeq, userSeq })
 * @param {number|null} idx - Redux toggleFileShareMessage용 메시지 인덱스 (없으면 null)
 * @param {Function|null} onDownloadStarted - 다운로드 성공 후 호출할 콜백 (선택)
 *        예: (idx) => store.dispatch(toggleFileShareMessage({ idx, isClickedBtn: true }))
 * @param {String} [kind="intensiveControl"] - 유형 (선택)
 * @returns {Promise<void>}
 * @since 2026-06-04.
 */
export const downloadFileUtil = async (url, originalFileName = null, isOverwriteAllowed = true, executeYn = "N", contentInfo = null, idx = null, onDownloadStarted = null, kind = "intensiveControl") => {
    // ── iOS WebView 분기 ──────────────────────────────────────────────────────
    // iOS 네이티브 브릿지를 통해 다운로드 처리 (fetch/Blob 방식 불가)
    if (isIOSWebView()) {
        const filename = getFinalFileName(url, originalFileName);
        const message = {
            action: "downloadFile",
            data: { url, idx, originalFileName: filename, kind, executeYn }
        };
        // iOS 네이티브 핸들러로 메시지 전달
        window.webkit.messageHandlers.screenShareHandler.postMessage(message);

        // iOS는 네이티브가 처리하므로 콜백만 호출하고 종료
        if (onDownloadStarted && idx !== null) {
            onDownloadStarted(idx);
        }
        return;
    }

    // ── Android WebView 분기 ─────────────────────────────────────────────────
    // Android 네이티브 브릿지를 통해 다운로드 처리
    if (isAndroidWebView()) {
        const filename = getFinalFileName(url, originalFileName);
        // Android 네이티브 fileDownload 메서드 호출
        window.fileDownload(url, filename, isOverwriteAllowed, executeYn, kind);

        if (onDownloadStarted && idx !== null) {
            onDownloadStarted(idx);
        }
        return;
    }

    // ── PBL 모드 브라우저 분기 ───────────────────────────────────────────────
    // PBL 모드 브라우저는 fetch Blob/ObjectURL 자동 클릭 다운로드가 깨질 수 있으므로
    // 원본 URL을 네이티브로 넘겨 HTTP 다운로드로 저장한다.
    if (getIsForPblModeBrowser() && window.eoapi !== undefined && window.eoapi !== null) {
        // 20260909 수정. PBL에서는 executeYn이 Y인 경우에만 아래 분기를 실행한다 ... by hjkim
        if (executeYn === "Y") {
            const filename = getFinalFileName(url, originalFileName);
            window.eoapi.extInvoke("downloadFile", [JSON.stringify({
                url,
                originalFileName: filename,
                isOverwriteAllowed,
                executeYn,
                kind,
                idx,
            })]);

            if (onDownloadStarted && idx !== null) {
                onDownloadStarted(idx);
            }
        }

        // 20260909 수정. PBL에서는 항상 return 처리하여 브라우저 다운로드를 막는다 ... by hjkim
        return;
    }

    // ── 웹 브라우저 분기 ────────────────────────────────────────────────────
    try {
        const filename = getFinalFileName(url, originalFileName);
        console.log("downloadFileUtil - final filename => ", filename);

        // URL 객체로 변환 (상대경로 대응)
        const requestUrl = new URL(url, ConstData.MAIN_HOST_ADD);
        console.log("downloadFileUtil - requestUrl => ", requestUrl);

        // 20260902 수정: 대용량 파일은 출처와 관계없이 브라우저에 URL을 직접 넘긴다.
        // fetch/Blob 방식은 파일 전체를 렌더러 메모리에 적재해 대용량 다운로드가 실패할 수 있다.
        const link = document.createElement("a");
        link.href = requestUrl.href;

        link.download = filename;       // download 속성이 있어야 클릭 시 다운로드로 동작
        link.style.display = "none";    // 화면에 보이지 않게

        // ObjectURL은 document 내에서만 유효하므로 먼저 appendChild
        document.body.appendChild(link);

        // 20260907 수정: 대용량 일반 웹 다운로드는 표준 링크 클릭으로 브라우저 다운로드를 시작한다.
        link.click();

        console.log("downloadFileUtil - 다운로드 시작됨");

        // 다운로드 시작 후 콜백 호출 (예: Redux 상태 업데이트)
        if (onDownloadStarted && idx !== null) {
            onDownloadStarted(idx);
        }

        // 20260907 수정: 대용량 요청이 브라우저 다운로드로 넘겨질 때까지 링크를 유지한다.

    } catch (err) {
        console.log("downloadFileUtil - err => ", err);
    }
}

/**
 * 현재 WebPage가 Pbl 웹 페이지인지 여부를 반환한다.
 * 
 * @param {enWebPage} currentPage - 현재 WebPage 값
 * @returns {Boolean} Pbl 웹 페이지인지 여부
 * @since 2025-07-30.
 */
export const checkPblPage = (currentPage) => {
    if (currentPage === enWebPage.LiveQRLoginPbl) return true;
    if (currentPage === enWebPage.LiveClassTalkPbl) return true;
    if (currentPage === enWebPage.Pbl) return true;
    if (currentPage === enWebPage.MixedClassQRLoginPbl) return true;
    if (currentPage === enWebPage.MixedClassLiveClassTalkPbl) return true;
    if (currentPage === enWebPage.MixedClassPbl) return true;
    return false;
}

/**
 * 현재 Pbl 모드 브라우저인지 여부를 반환한다.
 * 
 * @returns {Boolean} Pbl 모드 브라우저인지 여부
 * @since 2025-07-30.
 */
export const checkPblModeBrowser = () => {
    return getIsForPblModeBrowser();
}

/**
 * ClevisURL class
 *
 * URL 주소를 문자열에서 검색 및 URL 을 분석
 *
 * - 사용시 new 키워드로 객체를 생성하지 않고, ClevisURL.collect() 메서드와
 *   ClevisURL.parse() 메서드를 바로 호출하면 됩니다.
 * - parse() 메서드는 필요가 없을 경우 삭제하여 사용해도 무방합니다.
 * - LGPL 라이센스에 동의하에 얼마든지 수정하여 사용하실 수 있습니다.
 * - 단, 다른 사람이 개작된 소스의 재배포를 원할 경우 그 내용까지 포함하여 원시코드를
 *   공개해야 합니다.
 * - 또한, 재배포시 저작권 정보를 삭제해서는 안됩니다. 이 외에 수정 사항이 있을 경우
 *   저작권 정보를 덪붙일 수 있습니다.
 *
 * @author		KAi  (http://kais.tistory.com/)
 * @version		v0.2 (10-03-13)
 * @license		LPGL (http://korea.gnu.org/people/chsong/copyleft/lgpl.ko.html)
 */
/**
 * @usage
 *
 *   array   ClevisURL.collect( string );
 *     입력된 문자열에서 URL들을 찾아서 배열로 리턴
 *
 *   object  ClevisURL.parse( string );
 *     입력된 문자열(URL)을 분석하여 각 부분별 항목으로 리턴해줌
 *
 *
 * @history
 *
 *   v 0.2
 *     [10-03-13] 기존에 공개했던 정규식의 문제점을 개선하고 정확도를 높임
 *        [added] 클래스로 작성하고 URL 검출과 URL 분석할 수 있는 함수 추가
 *        [added] 로컬주소(file:///)도 검출하도록 추가
 *        [fixed] URL 도메인과 DOM 객체가 구분 안 되는 문제 수정 (ccTLD 추가)
 *        [fixed] HTTP 인증 정보가 제대로 캡처 안 되는 문제 수정
 *        [fixed] 쿼리스트링에 &*; 형식의 HTML 엔티티 문자도 포함되도록 수정
 *
 *   v 0.1
 *     [09-04-10] 간단히 URL 주소를 검색할 수 있는 코드 공개
 */
var ClevisURL = {
	// URL Pattern
	_patterns : {
		url : "(?:\\b(?:(?:(?:(ftp|https?|mailto|telnet):\\/\\/)?(?:((?:[\\w$\\-"
			+ "_\\.\\+\\!\\*\\'\\(\\),;\\?&=]|%[0-9a-f][0-9a-f])+(?:\\:(?:[\\w$"
			+ "\\-_\\.\\+\\!\\*\\'\\(\\),;\\?&=]|%[0-9a-f][0-9a-f])+)?)\\@)?((?"
			+ ":[\\d]{1,3}\\.){3}[\\d]{1,3}|(?:[a-z0-9]+\\.|[a-z0-9][a-z0-9\\-]+"
			+ "[a-z0-9]\\.)+(?:biz|com|info|name|net|org|pro|aero|asia|cat|coop|"
			+ "edu|gov|int|jobs|mil|mobi|museum|tel|travel|ero|gov|post|geo|cym|"
			+ "arpa|ac|ad|ae|af|ag|ai|al|am|an|ao|aq|ar|as|at|au|aw|ax|az|ba|bb|"
			+ "bd|be|bf|bg|bh|bi|bj|bm|bn|bo|br|bs|bt|bw|by|bz|ca|cc|cd|cf|cg|ch"
			+ "|ci|ck|cl|cm|cn|co|cr|cu|cv|cx|cy|cz|de|dj|dk|dm|do|dz|ec|ee|eg|e"
			+ "r|es|et|eu|fi|fj|fk|fm|fo|fr|ga|gd|ge|gf|gg|gh|gi|gl|gm|gn|gp|gq|"
			+ "gr|gs|gt|gu|gw|gy|hk|hm|hn|hr|ht|hu|id|ie|il|im|in|io|iq|ir|is|it"
			+ "|je|jm|jo|jp|ke|kg|kh|ki|km|kn|kr|kw|ky|kz|la|lb|lc|li|lk|lr|ls|l"
			+ "t|lu|lv|ly|ma|mc|me|md|mg|mh|mk|ml|mm|mn|mo|mp|mq|mr|ms|mt|mu|mv|"
			+ "mw|mx|my|mz|na|nc|ne|nf|ng|ni|nl|no|np|nr|nu|nz|om|pa|pe|pf|pg|ph"
			+ "|pk|pl|pn|pr|ps|pt|pw|py|qa|re|ro|rs|ru|rw|sa|sb|sc|sd|se|sg|sh|s"
			+ "i|sk|sl|sm|sn|sr|st|sv|sy|sz|tc|td|tf|tg|th|tj|tk|tl|tm|tn|to|tr|"
			+ "tt|tv|tw|tz|ua|ug|uk|us|uy|uz|va|vc|ve|vg|vi|vn|vu|wf|ws|ye|za|zm"
			+ "|zw)|localhost)\\b(?:\\:([\\d]+))?)|(?:(file):\\/\\/\\/?)?([a-z]:"
			+ "))(?:\\/((?:(?:[\\w$\\-\\.\\+\\!\\*\\(\\),;:@=ㄱ-ㅎㅏ-ㅣ가-힣]|%["
			+ "0-9a-f][0-9a-f]|&(?:nbsp|lt|gt|amp|cent|pound|yen|euro|sect|copy|"
			+ "reg);)*\\/)*)([^\\s\\/\\?:\\\"\\'<>\\|#]*)(?:[\\?:;]((?:\\b[\\w]+"
			+ "(?:=(?:[\\w\\$\\-\\.\\+\\!\\*\\(\\),;:=ㄱ-ㅎㅏ-ㅣ가-힣]|%[0-9a-f]"
			+ "[0-9a-f]|&(?:nbsp|lt|gt|amp|cent|pound|yen|euro|sect|copy|reg);)*"
			+ ")?\\&?)*))*(#[\\w\\-ㄱ-ㅎㅏ-ㅣ가-힣]+)?)?)",
		querystring: new RegExp("(\\b[\\w]+(?:=(?:[\\w\\$\\-\\.\\+\\!\\*\\(\\),;"
			+ ":=ㄱ-ㅎㅏ-ㅣ가-힣]|%[0-9a-f][0-9a-f]|&(?:nbsp|lt|gt|amp|cent|poun"
			+ "d|yen|euro|sect|copy|reg);)*)?)\\&?", "gi")
	},

	/**
	 * _process : 정규식 컴파일 후 검색
	 * @param	(string)		string			문자열
	 * @param	(string)		modifiers		정규식 수식어
	 * @return	(mixed)							정규식 결과 = [ array | null ]
	 */
	_process : function (string, modifiers)
	{
		if ( ! string) throw new Error(1, "입력값이 비어 있습니다.");

		var p = new RegExp(ClevisURL._patterns.url, modifiers);
		return string.match(p);
	},

	/**
	 * collect : 문장에서 여러 URL 주소 검색
	 * @param	(string)		text			URL 을 찾을 문장
	 * @return	(array)							배열로 리턴
	 */
	collect : function (text)
	{
		var r = ClevisURL._process(text, "gmi");
		return (r) ? r : [];
	},

	/**
	 * parse : 하나의 URL 주소를 분석
	 * @param	(string)		url				URL 주소
	 * @return	(object)						객체로 리턴
	 */
	parse : function (url, type)
	{
		var r = ClevisURL._process(url, "mi");

		if ( ! r) return {};

		// HTTP 인증정보
		if (r[2]) r[2] = r[2].split(":");

		// 쿼리스트링 분석
		if (r[9]) {
			r[9] = r[9].match(ClevisURL._patterns.querystring);
			for (var n = 0; n < r[9].length; n++) {
				r[9][n] = (r[9][n] ? r[9][n].replace(/\&$/, "").split("=") : []);
				if (r[9][n].length == 1)
					r[9][n][1] = "";
			}
		}

		// 프로토콜이 없을 경우 추가
		if ( ! r[1] && ! r[5]) {
			// 도메인이 없는 경우 로컬 파일 주소로 설정
			if ( ! r[3]) r[5] = "file";

			// E-Mail 인지 체크
			else if (r[0].match(new RegExp("^("+ r[2][0] +"@"+ r[3] +")$")))
				r[1] = "mailto";

			// 기타 기본 포트를 기준으로 프로토콜 설정.
			// 포트가 없을 경우 기본적으로 http 로 설정
			else {
				switch (r[4]) {
					case 21:	r[1] = "ftp"; break;
					case 23:	r[1] = "telnet"; break;
					case 443:	r[1] = "https"; break;
					case 80:
					default:	r[1] = "http"; break;
				}
			}

			r[0] = (r[1] ? r[1] +"://" : r[5] +":///")
				+ r[0];
		}

		return {
			"url"		: r[0],						// 전체 URL
			"protocol"	: (r[1] ? r[1] : r[5]),		// [ftp|http|https|mailto|telnet] | [file]
			"userid"	: (r[2] ? r[2][0] : ""),	// 아이디 : HTTP 인증 정보
			"userpass"	: (r[2] ? r[2][1] : ""),	// 비밀번호
			"domain"	: (r[3] ? r[3] : ""),		// 도메인주소
			"port"		: (r[4] ? r[4] : ""),		// 포트
			"drive"		: (r[6] ? r[6] : ""),		// "file" 프로토콜인 경우
			"directory"	: (r[7] ? r[7] : ""),		// 하위 디렉토리
			"filename"	: (r[8] ? r[8] : ""),		// 파일명
			"querys"	: (r[9] ? r[9] : ""),		// 쿼리스트링
			"anchor"	: (r[10] ? r[10] : "")		// Anchor
		};
	}
};// END: ClevisURL;
